import { z } from 'zod';
import { GUEST_DAY_OPTIONS, GuestDaysSchema, RoomRole, VALID_CHARACTER_IDS } from '../types.js';
import { PasswordSchema } from './password.schema.js';
const EmailSchema = z
    .string()
    .email('Invalid email format')
    .max(255, 'Email must be at most 255 characters');
/**
 * Island invitation sent by an admin (or a superadmin, bounded by §3.2 of
 * docs/PLATFORM_ROLES_AND_ORGANIZATIONS.md). Guests are time-boxed, so
 * `guestDays` is required for the guest role and rejected for the others.
 */
export const CreateInvitationSchema = z
    .object({
    email: EmailSchema,
    role: z.nativeEnum(RoomRole, { required_error: 'Role is required' }),
    guestDays: GuestDaysSchema.optional()
})
    .superRefine((data, ctx) => {
    if (data.role === RoomRole.GUEST && data.guestDays === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['guestDays'],
            message: `Guest invitations require guestDays (${GUEST_DAY_OPTIONS.join(', ')})`
        });
    }
    if (data.role !== RoomRole.GUEST && data.guestDays !== undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['guestDays'],
            message: 'guestDays is only valid for guest invitations'
        });
    }
});
/**
 * Account creation from an invitation link. The email is not part of the body:
 * it always comes from the invitation.
 */
export const RegisterWithInvitationSchema = z.object({
    token: z.string().min(1, 'Invitation token is required'),
    username: z
        .string()
        .min(3, 'Username must be at least 3 characters')
        .max(50, 'Username must be at most 50 characters')
        .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
    password: PasswordSchema,
    avatar: z
        .enum(VALID_CHARACTER_IDS, {
        errorMap: () => ({ message: 'Invalid character selection' })
    })
        .optional()
});
/** Platform invitation (superadmin only): an account with no island attached. */
export const PlatformInvitationSchema = z.object({
    email: EmailSchema
});
